import { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '../../../../app/store/hooks';
import { toChatRoomSummary } from '../../application/mapChat';
import type { ChatParticipant, ChatRoomSummary } from '../../application/types';
import {
  fetchChatParticipants,
  fetchChatRooms,
} from '../../infrastructure/api/chat.api';

export function useChatRooms(options?: { skip?: boolean }) {
  const skip = Boolean(options?.skip);
  const accessToken = useAppSelector((state) => state.authorization.accessToken);
  const currentUserId = useAppSelector((state) =>
    state.authorization.user?.id != null
      ? String(state.authorization.user.id)
      : '',
  );

  const [rooms, setRooms] = useState<ChatRoomSummary[]>([]);
  const [isLoading, setIsLoading] = useState(!skip);

  useEffect(() => {
    if (skip || !accessToken || !currentUserId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    (async () => {
      try {
        const chatRooms = await fetchChatRooms(accessToken);
        const peerIds = new Set<string>();
        for (const room of chatRooms) {
          for (const id of room.participants) {
            if (id !== currentUserId) {
              peerIds.add(id);
            }
          }
        }
        const participants = await fetchChatParticipants(
          Array.from(peerIds),
          accessToken,
        );
        const byId = new Map<string, ChatParticipant>(
          participants.map((p) => [p.id, p]),
        );

        const summaries: ChatRoomSummary[] = [];
        for (const room of chatRooms) {
          const peerId = room.participants.find((id) => id !== currentUserId);
          const peer = peerId ? byId.get(peerId) : undefined;
          if (peer) {
            summaries.push(toChatRoomSummary(room, peer));
          }
        }

        if (!cancelled) {
          setRooms(summaries);
        }
      } catch {
        if (!cancelled) {
          setRooms([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [skip, accessToken, currentUserId]);

  const currentUserIdValue = useMemo(() => currentUserId, [currentUserId]);

  return { rooms, isLoading, currentUserId: currentUserIdValue };
}
